// src/features/authz/TupleBrowser.tsx
// Browse stored relationship tuples matching a filter. Write/delete are
// intentionally not wired here — see the note rendered below the table and
// docs/ux/authz-ia.md section 4 for why.
// See docs/ux/authz-ia.md section 4.

import { useState } from 'react';
import { useI18n, useAuthz } from '@react/hooks';
import { Alert, Button, Input, ErrorAlert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import type { Tuple } from '@core/types/authz';

export function TupleBrowser() {
  const { t } = useI18n();
  const store = useAuthz();
  const [user, setUser] = useState('');
  const [relation, setRelation] = useState('');
  const [object, setObject] = useState('');
  const [tuples, setTuples] = useState<Tuple[] | null>(null);
  const [continuationToken, setContinuationToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const runQuery = async (token = '') => {
    setIsLoading(true);
    try {
      const r = await store.ReadTuples({
        filter: { user, relation, object },
        page_size: 50,
        continuation_token: token,
        consistency: undefined,
      });
      setTuples(r.tuples?.items ?? []);
      setContinuationToken(r.continuation_token ?? '');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runQuery('');
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <Input label={t('authz.check.user')} value={user} onChange={(e) => setUser(e.target.value)} placeholder="user:bob" className="w-48" />
        <Input label={t('authz.check.relation')} value={relation} onChange={(e) => setRelation(e.target.value)} placeholder="viewer" className="w-32" />
        <Input label={t('authz.check.object')} value={object} onChange={(e) => setObject(e.target.value)} placeholder="document:readme" className="w-48" />
        <Button type="submit" size="sm" disabled={isLoading}>{isLoading ? t('common.loading') : t('authz.tuples.browse')}</Button>
      </form>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {tuples && (
        <>
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('authz.check.user')}</TableHead>
                <TableHead>{t('authz.check.relation')}</TableHead>
                <TableHead>{t('authz.check.object')}</TableHead>
                <TableHead>{t('authz.tuples.col.timestamp')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tuples.length === 0 ? (
                <TableEmpty colSpan={4} message={t('authz.tuples.empty')} />
              ) : (
                tuples.map((tup, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{tup.key?.user}</TableCell>
                    <TableCell className="font-mono text-xs">{tup.key?.relation}</TableCell>
                    <TableCell className="font-mono text-xs">{tup.key?.object}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{tup.timestamp}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {continuationToken && (
            <Button variant="ghost" size="sm" onClick={() => runQuery(continuationToken)} disabled={isLoading}>
              {t('common.loadMore')}
            </Button>
          )}
        </>
      )}

      <Alert variant="info">
        {t('authz.tuples.writeDeleteInternalNote')}
      </Alert>
    </div>
  );
}
